/**
 * EstablishmentsList - Manages organization establishments (DBAs)
 * Phase 9.18d/e: Establishments for invoices
 */
import { useState, useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { InlineEditActions } from '@/components/ui/InlineEditActions';
import { AccountingContactsList } from '@/components/accounting-contacts/AccountingContactsList';
import { InvoiceChangeReview } from '@/components/invoices/InvoiceChangeReview';
import type { Establishment } from '@/lib/schemas';
import type { InvoiceChangeIntent } from '@/lib/invoiceSelectiveHistoryContracts';

export interface EstablishmentEditData {
  name: string;
  legal_name?: string | null;  // Phase 9.23a: Override organization legal_name
  is_default: boolean;
  address?: string | null;  // v0.12.26: Establishment-level billing address
}

interface EstablishmentsListProps {
  establishments: Establishment[];
  onAddEstablishment: (data: { name: string; legal_name?: string | null; is_default: boolean; address?: string | null }) => void;
  onDeleteEstablishment: (id: number) => void;
  isAddingEstablishment?: boolean;
  className?: string;
  organizationLegalName?: string | null;  // Phase 9.26: Show effective legal name
  organizationId?: string;  // For per-establishment accounting contacts
}

/**
 * Displays a list of organization establishments (DBAs) with add/edit/delete capabilities.
 */
export function EstablishmentsList({
  establishments,
  onAddEstablishment,
  onDeleteEstablishment,
  isAddingEstablishment,
  className,
  organizationLegalName,
  organizationId,
}: EstablishmentsListProps) {
  const [isAdding, setIsAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newLegalName, setNewLegalName] = useState('');  // Phase 9.23a
  const [newAddress, setNewAddress] = useState('');  // v0.12.26: Establishment address
  const [editingId, setEditingId] = useState<number | null>(null);
  const [pendingReview, setPendingReview] = useState<{
    intent: InvoiceChangeIntent;
    defaultDeferred: boolean;
  } | null>(null);
  const [reviewStatus, setReviewStatus] = useState<string | null>(null);

  const handleSaveEdit = (establishment: Establishment, data: EstablishmentEditData): boolean => {
    if (!organizationId) return false;
    const fields: Partial<Record<'legal_name' | 'dba' | 'billing_address', string>> = {};
    const nextName = data.name.trim();
    if (nextName !== establishment.name) fields.dba = nextName;
    const nextLegalName = data.legal_name?.trim() || '';
    if (nextLegalName !== (establishment.legal_name ?? '')) fields.legal_name = nextLegalName;
    const nextAddress = data.address?.trim() || '';
    if (nextAddress !== (establishment.address ?? '')) fields.billing_address = nextAddress;
    const defaultChanged = data.is_default !== Boolean(establishment.is_default);
    if (Object.keys(fields).length === 0 && !defaultChanged) {
      setEditingId(null);
      return true;
    }

    const correction: InvoiceChangeIntent | undefined = Object.keys(fields).length > 0
      ? { intent: 'shared_correction', organization_id: organizationId,
        establishment_id: establishment.id, fields }
      : undefined;
    const defaultChange: InvoiceChangeIntent | undefined = defaultChanged
      ? { intent: 'identity_default', organization_id: organizationId, future_defaults: true,
        default_level: 'this_client', default_field: 'dba',
        default_establishment_id: data.is_default ? establishment.id : null }
      : undefined;
    setPendingReview({
      intent: correction ?? defaultChange!,
      defaultDeferred: Boolean(correction && defaultChange),
    });
    setReviewStatus(null);
    return false;
  };

  const handleReviewApplied = () => {
    if (!pendingReview) return;
    if (pendingReview.defaultDeferred) {
      setReviewStatus('The DBA identity correction was applied. The default choice was left unchanged; edit this DBA again to review that future-default change separately.');
    }
    setEditingId(null);
    setPendingReview(null);
  };

  const establishmentCount = establishments.length;
  const hasEstablishments = establishmentCount > 0;

  const handleAdd = () => {
    if (newName.trim()) {
      onAddEstablishment({
        name: newName.trim(),
        legal_name: newLegalName.trim() || null,  // Phase 9.23a
        address: newAddress.trim() || null,  // v0.12.26
        is_default: false,
      });
      setNewName('');
      setNewLegalName('');
      setNewAddress('');
      setIsAdding(false);
    }
  };

  const handleCancelAdd = () => {
    setNewName('');
    setNewLegalName('');
    setNewAddress('');
    setIsAdding(false);
  };

  return (
    <div className={className}>
      {reviewStatus && <p role="status" className="mb-3 rounded border border-blue-200 bg-blue-50 p-3 text-sm" data-testid="establishment-review-status">{reviewStatus}</p>}
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold">DBAs</h2>
          <p className="text-sm text-gray-500 mt-1">
            {hasEstablishments
              ? `${establishmentCount} ${establishmentCount === 1 ? 'DBA' : 'DBAs'}`
              : 'Add business names for invoicing'}
          </p>
        </div>
        {!isAdding && (
          <Button size="sm" onClick={() => setIsAdding(true)}>
            Add DBA
          </Button>
        )}
      </div>

      {/* Add Form */}
      {isAdding && (
        <div className="border rounded-lg p-4 bg-gray-50 mb-4">
          <div className="space-y-3">
            <div>
              <label htmlFor="new-establishment-name" className="text-sm font-medium text-gray-700">
                Name (DBA)
              </label>
              <Input
                id="new-establishment-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g., Banks Architecture West"
                className="mt-1"
                autoFocus
              />
            </div>
            <div>
              <label htmlFor="new-establishment-legal-name" className="text-sm font-medium text-gray-700">
                Legal Name <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <Input
                id="new-establishment-legal-name"
                value={newLegalName}
                onChange={(e) => setNewLegalName(e.target.value)}
                placeholder="e.g., 123456 BC Ltd."
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave blank to use the client's legal name on invoices
              </p>
            </div>
            <div>
              <label htmlFor="new-establishment-address" className="text-sm font-medium text-gray-700">
                Billing Address <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea
                id="new-establishment-address"
                value={newAddress}
                onChange={(e) => setNewAddress(e.target.value)}
                placeholder="e.g., 379 Adelaide St West, Toronto ON M5V 1S5"
                rows={2}
                className="mt-1 flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
              <p className="text-xs text-gray-500 mt-1">
                Used as the billing address on invoices that select this DBA. Leave blank to fall back to the project or client address.
              </p>
            </div>
            <p className="text-xs text-gray-600" data-testid="new-establishment-default-review-note">
              New DBAs start without becoming the client default. After adding one, choose Edit to set it as the
              default through billing review.
            </p>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAdd} disabled={!newName.trim() || isAddingEstablishment}>
                {isAddingEstablishment ? 'Adding...' : 'Add'}
              </Button>
              <Button size="sm" variant="outline" onClick={handleCancelAdd}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Establishments List */}
      {hasEstablishments ? (
        <div className="space-y-3">
          {establishments.map((establishment) => (
            <EstablishmentCard
              key={establishment.id}
              establishment={establishment}
              isEditing={editingId === establishment.id}
              onStartEdit={() => setEditingId(establishment.id)}
              onSaveEdit={(data) => handleSaveEdit(establishment, data)}
              onCancelEdit={() => setEditingId(null)}
              onDelete={() => onDeleteEstablishment(establishment.id)}
              organizationLegalName={organizationLegalName}
              organizationId={organizationId}
            />
          ))}
        </div>
      ) : !isAdding ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <p className="text-gray-500 text-sm">No DBAs added yet</p>
          <p className="text-gray-400 text-xs mt-1">
            Add DBAs to show different business names on invoices
          </p>
        </div>
      ) : null}
      {pendingReview && (
        <>
          <p className="sr-only" data-testid="establishment-review-context">
            Review this shared DBA identity correction. It changes billing identity for invoices using this DBA;
            assigning a different payer to named projects is a separate project assignment.
          </p>
          <InvoiceChangeReview
            intent={pendingReview.intent}
            onClose={() => setPendingReview(null)}
            onApplied={handleReviewApplied}
          />
        </>
      )}
    </div>
  );
}

interface EstablishmentCardProps {
  establishment: Establishment;
  isEditing: boolean;
  onStartEdit: () => void;
  onSaveEdit: (data: EstablishmentEditData) => boolean;
  onCancelEdit: () => void;
  onDelete: () => void;
  organizationLegalName?: string | null;  // Phase 9.26: Show effective legal name
  organizationId?: string;
}

function EstablishmentCard({
  establishment,
  isEditing,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onDelete,
  organizationLegalName,
  organizationId,
}: EstablishmentCardProps) {
  // Phase 9.26: Calculate effective legal name (establishment overrides organization)
  const effectiveLegalName = establishment.legal_name || organizationLegalName;
  const [editForm, setEditForm] = useState<EstablishmentEditData>({
    name: establishment.name,
    legal_name: establishment.legal_name || '',  // Phase 9.23a
    address: establishment.address || '',  // v0.12.26
    is_default: establishment.is_default ?? false,
  });
  useEffect(() => {
    if (isEditing) {
      setEditForm({
        name: establishment.name,
        legal_name: establishment.legal_name || '',  // Phase 9.23a
        address: establishment.address || '',  // v0.12.26
        is_default: establishment.is_default ?? false,
      });
    }
  }, [isEditing, establishment]);

  const commitSave = () => onSaveEdit({
    ...editForm,
    legal_name: editForm.legal_name?.trim() || null,  // Normalize empty string to null
    address: editForm.address?.trim() || null,  // v0.12.26: normalize empty to null
  });

  const handleSaveClick = () => { commitSave(); };

  if (isEditing) {
    return (
      <div className="border rounded-lg p-4 bg-white shadow-sm ring-2 ring-blue-500">
        <div className="space-y-3">
          <div>
            <label htmlFor={`est-name-${establishment.id}`} className="text-sm font-medium text-gray-700">
              Name (DBA)
            </label>
            <Input
              id={`est-name-${establishment.id}`}
              value={editForm.name}
              onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
              className="mt-1"
            />
          </div>
          <div>
            <label htmlFor={`est-legal-name-${establishment.id}`} className="text-sm font-medium text-gray-700">
              Legal Name <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <Input
              id={`est-legal-name-${establishment.id}`}
              value={editForm.legal_name || ''}
              onChange={(e) => setEditForm((prev) => ({ ...prev, legal_name: e.target.value || null }))}
              placeholder="e.g., 123456 BC Ltd."
              className="mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">
              Leave blank to use the client's legal name on invoices
            </p>
            <div
              className="mt-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
              data-testid={`est-${establishment.id}-legal-name-cascade-warning`}
            >
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-700" />
              <span>
                This DBA identity can affect billing details on existing invoices that use it. Save opens a review
                where you choose which affected invoices to update; no change is written before review. Changing the
                payer for named projects is a separate project assignment.
              </span>
            </div>
          </div>
          <div>
            <label htmlFor={`est-address-${establishment.id}`} className="text-sm font-medium text-gray-700">
              Billing Address <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              id={`est-address-${establishment.id}`}
              value={editForm.address || ''}
              onChange={(e) => setEditForm((prev) => ({ ...prev, address: e.target.value || null }))}
              placeholder="e.g., 379 Adelaide St West, Toronto ON M5V 1S5"
              rows={2}
              className="mt-1 flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
            <p className="text-xs text-gray-500 mt-1">
              Used as the billing address on invoices that select this DBA. Leave blank to fall back to the project or client address.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id={`est-default-${establishment.id}`}
              checked={editForm.is_default}
              onCheckedChange={(checked) => setEditForm((prev) => ({ ...prev, is_default: checked === true }))}
            />
            <label htmlFor={`est-default-${establishment.id}`} className="text-sm text-gray-700">
              Set as default for new invoices
            </label>
          </div>
          <div className="flex gap-2 mt-4">
            <Button size="sm" onClick={handleSaveClick} data-testid={`est-${establishment.id}-save`}>Save</Button>
            <InlineEditActions onCancel={onCancelEdit} showEnterHint={false} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-4 bg-white shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium">{establishment.name}</span>
            {establishment.is_default && <Badge variant="default">Default</Badge>}
          </div>
          {/* Phase 9.26: Show legal name with context about where it comes from */}
          {effectiveLegalName && (
            <p className="text-sm text-gray-500 mt-1">
              {establishment.legal_name ? (
                <>Legal: {establishment.legal_name}</>
              ) : (
                <>Legal: {organizationLegalName} <span className="text-gray-400">(from client)</span></>
              )}
            </p>
          )}
          {/* v0.12.26: Show establishment-level billing address */}
          {establishment.address && (
            <p className="text-sm text-gray-500 mt-1 whitespace-pre-line">
              Address: {establishment.address}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onStartEdit}>
            Edit
          </Button>
          <Button variant="destructive" size="sm" onClick={onDelete}>
            Delete
          </Button>
        </div>
      </div>
      {organizationId && (
        <div className="mt-3">
          <AccountingContactsList
            orgId={organizationId}
            establishmentId={establishment.id}
            overrideNotice={true}
            testId={`est-${establishment.id}-accounting-contacts`}
          />
        </div>
      )}
    </div>
  );
}
